import React, { useState, useEffect, useCallback } from 'react';

import "../../../css/components/OrderInfo/EditOrderModal/EditOrderModal.css";

import { roleConfig } from '../../../constants/roleConfig';
import { useUser } from '../../../hooks/useUser';
import RoleGuard from '../../RoleGuard/RoleGuard'
import { Message } from '../..';

import { CreateDeliverModalList } from '../../../constants/DeliverModal/DeliverModal';

import { getSMSCode } from '../../../service/api/SMS/apiSMS';

const CreateDeliverModal = ({ onSubmit, onCancel, companies = [], mode: propMode = 'personal' }) => {
    const { role: currentUserRole } = useUser();
    // 如果 propMode = 'deliver'，讓用戶選擇 mode；否則直接使用 propMode
    const [selectedMode, setSelectedMode] = useState(propMode === 'deliver' ? 'personal' : propMode);
    const mode = propMode === 'deliver' ? selectedMode : propMode;

    const [form, setForm] = useState({
        email: "",
        name: "",
        phone: "",
        company: "",
        status: 1,
        role: roleConfig['員工'], // 預設為員工
        password: "",
        confirmPassword: "",
        lineId: "",
        verifyCode: "",
    });
    const [newCompanyInput, setNewCompanyInput] = useState('');
    const [showNewCompanyInput, setShowNewCompanyInput] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    
    // SMS 驗證相關狀態
    const [smsCountdown, setSmsCountdown] = useState(0);
    const [smsSending, setSmsSending] = useState(false);

    // SMS 倒數計時
    useEffect(() => {
        if (smsCountdown > 0) {
            const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [smsCountdown]);

    // 發送簡訊驗證碼
    const handleSendSMS = useCallback(async () => {
        if (!form.phone || !/^09\d{8}$/.test(form.phone)) {
            Message('請先輸入正確的手機號碼', 'warning');
            return;
        }
        
        setSmsSending(true);
        try {
            const response = await getSMSCode({ phone: form.phone });
            if (response.data.status === 1) {
                Message('驗證碼已發送');
                setSmsCountdown(30);
            } else {
                Message(response.data.message || '發送失敗', 'warning');
            }
        } catch (error) {
            Message('發送驗證碼失敗', 'error');
        } finally {
            setSmsSending(false);
        }
    }, [form.phone]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleCompanyChange = (e) => {
        const value = e.target.value;
        if (value === 'new') {
            setShowNewCompanyInput(true);
            setForm(prev => ({ ...prev, company: '' }));
        } else {
            setShowNewCompanyInput(false);
            setForm(prev => ({ ...prev, company: value }));
        }
    };

    const handleAddNewCompany = () => {
        if (newCompanyInput.trim()) {
            setForm(prev => ({ ...prev, company: newCompanyInput }));
            setNewCompanyInput('');
            setShowNewCompanyInput(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setPasswordError('');

        // 驗證密碼匹配
        if (form.password && form.confirmPassword && form.password !== form.confirmPassword) {
            setPasswordError('密碼不一致，請重新輸入');
            Message('密碼不一致，請重新輸入', 'warning');
            return;
        }

        // 個人模式下，company 欄位應為空或 null
        const submitData = {
            ...form,
            company: (mode === 'personal' || !form.company) ? null : form.company
        };
        
        // 移除 confirmPassword，只提交 password
        delete submitData.confirmPassword;
        
        console.log(submitData);
        onSubmit(submitData);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="custom-modal-backdrop">
                <div className="custom-modal">
                    <div className="custom-modal-header">
                        <h5 className="modal-title">新增司機</h5>
                        <button className="btn-close" onClick={onCancel}></button>
                    </div>

                    <div className="custom-modal-body">
                        {/* 密碼錯誤提示 */}
                        {passwordError && (
                            <div className="alert alert-danger" role="alert">
                                {passwordError}
                            </div>
                        )}
                        {/* 模式選擇 - 只在 propMode = 'deliver' 時顯示 */}
                        {propMode === 'deliver' && (
                            <div className="input-group mb-3">
                                <span className="input-group-text">司機類型</span>
                                <select
                                    className="form-select"
                                    value={selectedMode}
                                    onChange={(e) => {
                                        setSelectedMode(e.target.value);
                                        // 清空所有表單資訊
                                        setForm({
                                            email: "",
                                            name: "",
                                            phone: "",
                                            company: "",
                                            status: 1,
                                        });
                                    }}
                                >
                                    <option value="personal">個人司機</option>
                                    <option value="company">公司司機</option>
                                </select>
                            </div>
                        )}
                        {/* 基本資訊 */}
                        {CreateDeliverModalList.map(field => {
                            // 公司欄位使用選單替代
                            if (field.name === 'company') {
                                return null;
                            }

                            // select 類型欄位
                            if (field.type === 'select') {
                                // 角色欄位：過濾出不大於當前使用者角色的選項
                                let filteredOptions = field.options || [];
                                if (field.name === 'role' && currentUserRole) {
                                    filteredOptions = field.options?.filter(opt => opt.value <= currentUserRole) || [];
                                }

                                return (
                                    <div className="input-group mb-3" key={field.name}>
                                        <span className="input-group-text">
                                            {field.label}
                                            {field.required && <span className="required-star">*</span>}
                                        </span>
                                        <select
                                            className="form-select"
                                            name={field.name}
                                            value={form[field.name] || ''}
                                            onChange={handleChange}
                                            required={field.required}
                                        >
                                            <option disabled value="">請選擇{field.label}</option>
                                            {filteredOptions?.map(opt => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            }

                            // text/email/password 類型欄位
                            return (
                                <div className="input-group mb-3" key={field.name}>
                                    <span className="input-group-text">
                                        {field.label}
                                        {field.required && <span className="required-star">*</span>}
                                    </span>
                                    <input
                                        type={field.type}
                                        className="form-control"
                                        name={field.name}
                                        value={form[field.name]}
                                        onChange={handleChange}
                                        placeholder={field.placeholder || ""}
                                        required={field.required}
                                        pattern={field.pattern}
                                        title={field.title}
                                        autoComplete={field.name === 'password' ? 'new-password' : ''}
                                    />
                                </div>
                            );
                        })}

                        {/* 公司選單 - 只在公司模式顯示 */}
                        {mode === 'company' && (
                            <div className="input-group mb-3">
                                <span className="input-group-text">
                                    公司
                                    <span className="required-star">*</span>
                                </span>
                                <select
                                    className="form-select"
                                    value={form.company || ''}
                                    onChange={handleCompanyChange}
                                    required
                                >
                                    <option value="">請選擇公司</option>
                                    {companies.map(company => (
                                        <option key={company.id} value={company.name}>
                                            {company.name}
                                        </option>
                                    ))}
                                     <RoleGuard requiredRole={roleConfig['管理者']}>{propMode !== 'deliver' && <option value="new">+ 新增公司</option>}</RoleGuard>
                                </select>
                            </div>
                        )}

                        {/* 公司選單 - 個人模式時隱藏 */}
                        {mode === 'personal' && (
                            <input type="hidden" name="company" value="" />
                        )}

                        {/* 新增公司輸入框 */}
                        {showNewCompanyInput && (
                            <div className="input-group mb-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="輸入新公司名稱"
                                    value={newCompanyInput}
                                    onChange={(e) => setNewCompanyInput(e.target.value)}
                                />
                                <button
                                    className="btn btn-outline-primary"
                                    type="button"
                                    onClick={handleAddNewCompany}
                                >
                                    新增
                                </button>
                                <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={() => {
                                        setShowNewCompanyInput(false);
                                        setNewCompanyInput('');
                                    }}
                                >
                                    取消
                                </button>
                            </div>
                        )}

                        {/* 簡訊驗證碼 */}
                        <div className="input-group mb-3">
                            <span className="input-group-text">
                                簡訊驗證碼
                                <span className="required-star">*</span>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                name="verifyCode"
                                value={form.verifyCode}
                                onChange={handleChange}
                                placeholder="請輸入簡訊驗證碼"
                                required
                                maxLength={6}
                            />
                            <button
                                className="btn btn-outline-primary"
                                type="button"
                                onClick={handleSendSMS}
                                disabled={smsCountdown > 0 || smsSending || !form.phone}
                            >
                                {smsSending ? '發送中...' : smsCountdown > 0 ? `${smsCountdown}秒後重發` : '發送驗證碼'}
                            </button>
                        </div>

                        {/* 員工狀態 */}
                        <div className="input-group mb-3">
                            <span className="input-group-text">員工狀態</span>
                            <select
                                className="form-select"
                                value={form.status === 1 ? "working" : form.status === 0 ? "resignation" : "suspension"}
                                onChange={e => setForm(prev => ({ ...prev, status: e.target.value === "working" ? 1 : e.target.value === "resignation" ? 0 : 2 }))}
                            >
                                <option value="resignation">離職</option>
                                <option value="suspension">停職</option>
                                <option value="working">在職</option>
                            </select>
                        </div>
                    </div>

                    <div className="custom-modal-footer">
                        <button className="btn btn-secondary" onClick={onCancel}>取消</button>
                        <button className="btn btn-gradient-primary" type="submit">儲存</button>
                    </div>
                </div>
            </div>
        </form>
    );
};

export { CreateDeliverModal };
